import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'

import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'

import {
    ActionsRowResponsive,
    buttonSx,
    chipSx,
    classToFComponent,
    EditorBody,
    EditorSideColumn,
    FCClassContext,
    Field,
    FieldHelp,
    FieldLabel,
    inputSx,
    KeyDivider,
    KeyRow,
    KeyValues,
    PanelCard,
    PanelCardBody,
    SkeletonBlock,
    SwitchRow,
    switchSx
} from 'src/utils/views'

import { EmailConfigScope, SmtpConfig } from '../scopes'
import { Banner, CardHead, FieldGrid, SettingsStack } from 'src/utils/views/modules/admin-configurations'

const LOG = Logger.get('MailConfig-VIEW')

const useStyles = makeStyles()({
    view: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        flexGrow: 1
    },
    input: {
        ...inputSx
    },
    hostRow: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 2.4fr) minmax(120px, .7fr)',
        gap: 14,
        minWidth: 0,
        '@media (max-width: 720px)': {
            gridTemplateColumns: 'minmax(0, 1fr)'
        }
    },
    switchGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: 14
    }
})

type EmailConfigViewProps = {
    className?: string
    scope: EmailConfigScope
}

class EmailConfigClass implements FCClassContext<EmailConfigViewProps> {
    // :: emissors
    readonly emitOnSave = () => {
        this.scope.onSave().catch(LOG.caught)
    }

    readonly handleConfigChange = (field: keyof SmtpConfig, value: unknown) => {
        this.scope.onConfigChanged({ [field]: value }).catch(LOG.caught)
    }

    // :: Fields
    scope!: EmailConfigScope
    sytles!: ReturnType<typeof useStyles>

    // :: Methods

    onSyncState({ scope }: EmailConfigViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: EmailConfigViewProps) {
        this.scope = bindUpdate(React, scope)
        const classes = this.sytles.classes

        if (scope.waiting) {
            return (
                <div className={clsx(classes.view, className)}>
                    <EditorBody>
                        <SettingsStack>
                            <SkeletonBlock sx={{ height: 300 }} />
                            <SkeletonBlock sx={{ height: 220 }} />
                        </SettingsStack>
                        <EditorSideColumn>
                            <SkeletonBlock sx={{ height: 260 }} />
                        </EditorSideColumn>
                    </EditorBody>
                </div>
            )
        }

        return (
            <div className={clsx(classes.view, className)}>
                <EditorBody>
                    <SettingsStack>
                        {this.renderServerCard()}
                        {this.renderTemplatesCard()}
                        <ActionsRowResponsive>
                            <Button sx={buttonSx('primary')} startIcon={<SaveOutlinedIcon />} onClick={this.emitOnSave}>
                                Salvar configuração
                            </Button>
                        </ActionsRowResponsive>
                    </SettingsStack>
                    <EditorSideColumn>{this.renderSummary()}</EditorSideColumn>
                </EditorBody>
            </div>
        )
    }

    renderServerCard() {
        const { classes } = this.sytles
        const config = this.scope.config

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Servidor de envio</h3>
                        <p className="subtitle">Endereço, transporte e credenciais usados para enviar e-mails.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <div className={classes.hostRow}>
                        <Field>
                            <FieldLabel htmlFor="smtpHost">Host</FieldLabel>
                            <TextField
                                id="smtpHost"
                                className={classes.input}
                                fullWidth
                                required
                                value={config.host ?? ''}
                                onChange={(e) => this.handleConfigChange('host', e.target.value)}
                                placeholder="smtp.provedor.com.br"
                            />
                            <FieldHelp>Servidor SMTP que receberá as mensagens do sistema.</FieldHelp>
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="smtpPort">Porta</FieldLabel>
                            <TextField
                                id="smtpPort"
                                className={classes.input}
                                fullWidth
                                required
                                type="number"
                                value={config.port ?? ''}
                                onChange={(e) => this.handleConfigChange('port', Number(e.target.value))}
                            />
                            <FieldHelp>587 para STARTTLS, 465 para SSL direto.</FieldHelp>
                        </Field>
                    </div>

                    <div className={classes.switchGrid}>
                        <SwitchRow>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={config.secure || false}
                                onChange={(e) => this.handleConfigChange('secure', e.target.checked)}
                                slotProps={{
                                    input: { 'aria-label': 'Conexão SSL' }
                                }}
                            />
                            <div>
                                <b>Conexão SSL</b>
                                <FieldHelp>A sessão começa criptografada, sem negociação em texto claro.</FieldHelp>
                            </div>
                        </SwitchRow>
                        <SwitchRow>
                            <Switch
                                sx={switchSx}
                                size="small"
                                checked={config.tls || false}
                                onChange={(e) => this.handleConfigChange('tls', e.target.checked)}
                                slotProps={{
                                    input: { 'aria-label': 'Negociar TLS' }
                                }}
                            />
                            <div>
                                <b>Negociar TLS</b>
                                <FieldHelp>A conexão é elevada para criptografada após o STARTTLS.</FieldHelp>
                            </div>
                        </SwitchRow>
                    </div>

                    <FieldGrid>
                        <Field>
                            <FieldLabel htmlFor="smtpUser">Usuário</FieldLabel>
                            <TextField
                                id="smtpUser"
                                className={classes.input}
                                fullWidth
                                value={config.user || ''}
                                onChange={(e) => this.handleConfigChange('user', e.target.value)}
                                autoComplete="off"
                            />
                        </Field>
                        <Field>
                            <FieldLabel htmlFor="smtpPass">Senha</FieldLabel>
                            <TextField
                                id="smtpPass"
                                className={classes.input}
                                fullWidth
                                type="password"
                                value={config.pass || ''}
                                onChange={(e) => this.handleConfigChange('pass', e.target.value)}
                                autoComplete="new-password"
                            />
                        </Field>
                    </FieldGrid>

                    <Field>
                        <FieldLabel htmlFor="smtpFrom">Remetente</FieldLabel>
                        <TextField
                            id="smtpFrom"
                            className={classes.input}
                            fullWidth
                            value={config.from || ''}
                            onChange={(e) => this.handleConfigChange('from', e.target.value)}
                            placeholder="LifesHub &lt;nao-responda@lifeshub.com.br&gt;"
                        />
                        <FieldHelp>É o endereço que o usuário vê ao receber a mensagem.</FieldHelp>
                    </Field>
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderTemplatesCard() {
        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Mensagens automáticas</h3>
                        <p className="subtitle">Qual template o sistema envia em cada evento de conta.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    {this.scope.templates?.length === 0 && (
                        <Banner tone="warning">
                            Nenhum template cadastrado. Crie os modelos na aba &quot;Templates de e-mail&quot; para
                            poder vinculá-los aos eventos abaixo.
                        </Banner>
                    )}

                    {this.renderTemplateField(
                        'welcomeTemplate',
                        'Boas-vindas',
                        'template_welcome_seq',
                        'Enviado quando um usuário novo é cadastrado.'
                    )}
                    {this.renderTemplateField(
                        'recoveryTemplate',
                        'Recuperação de senha',
                        'template_change_password_request_seq',
                        'Enviado quando o usuário pede uma nova senha em "Esqueci minha senha".'
                    )}
                    {this.renderTemplateField(
                        'changedTemplate',
                        'Senha alterada',
                        'template_password_changed_seq',
                        'Enviado para confirmar ao usuário que a senha dele mudou.'
                    )}
                </PanelCardBody>
            </PanelCard>
        )
    }

    renderTemplateField(id: string, label: string, field: keyof SmtpConfig, help: string) {
        const { classes } = this.sytles

        return (
            <Field>
                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                <TextField
                    id={id}
                    className={classes.input}
                    select
                    fullWidth
                    value={(this.scope.config[field] as number | undefined) ?? ''}
                    onChange={(evt) =>
                        this.handleConfigChange(field, !evt.target.value ? undefined : Number(evt.target.value))
                    }
                    slotProps={{
                        select: { displayEmpty: true }
                    }}
                >
                    <MenuItem value="">Selecione</MenuItem>
                    {this.scope.templates?.map((tmp) => (
                        <MenuItem key={tmp.seq} value={tmp.seq}>
                            {tmp.name}
                        </MenuItem>
                    ))}
                </TextField>
                <FieldHelp>{help}</FieldHelp>
            </Field>
        )
    }

    renderSummary() {
        const config = this.scope.config
        const transport = config.secure ? 'SSL' : config.tls ? 'TLS' : 'Sem criptografia'

        return (
            <PanelCard elevation={0}>
                <CardHead>
                    <div>
                        <h3>Resumo do envio</h3>
                        <p className="subtitle">O que está gravado hoje para este servidor.</p>
                    </div>
                </CardHead>
                <PanelCardBody>
                    <KeyValues>
                        <KeyRow>
                            <div className="key">Servidor</div>
                            <div className="value">{config.host || '—'}</div>
                        </KeyRow>
                        <KeyRow>
                            <div className="key">Porta</div>
                            <div className="value">{config.port ?? '—'}</div>
                        </KeyRow>
                        <KeyRow>
                            <div className="key">Transporte</div>
                            <div className="value">
                                <Chip
                                    size="small"
                                    label={transport}
                                    sx={chipSx(config.secure || config.tls ? 'green' : 'orange')}
                                />
                            </div>
                        </KeyRow>
                        <KeyRow>
                            <div className="key">Autenticação</div>
                            <div className="value">{config.user || 'Sem usuário'}</div>
                        </KeyRow>
                        <KeyRow>
                            <div className="key">Remetente</div>
                            <div className="value">{config.from || '—'}</div>
                        </KeyRow>
                    </KeyValues>

                    <KeyDivider />

                    <KeyValues>
                        <KeyRow>
                            <div className="key">Boas-vindas</div>
                            <div className="value">{this.templateName(config.template_welcome_seq)}</div>
                        </KeyRow>
                        <KeyRow>
                            <div className="key">Recuperação</div>
                            <div className="value">
                                {this.templateName(config.template_change_password_request_seq)}
                            </div>
                        </KeyRow>
                        <KeyRow>
                            <div className="key">Senha alterada</div>
                            <div className="value">{this.templateName(config.template_password_changed_seq)}</div>
                        </KeyRow>
                    </KeyValues>
                </PanelCardBody>
            </PanelCard>
        )
    }

    templateName(seq?: number) {
        if (!seq) {
            return <span style={{ color: 'var(--muted-2)', fontWeight: 400 }}>Não definido</span>
        }

        return this.scope.templates?.find((tmp) => tmp.seq === seq)?.name ?? `#${seq}`
    }
}

export const EmailConfigView = classToFComponent(EmailConfigClass, React)
export default EmailConfigView
